import type { TFunction } from 'i18next';
import moment from 'moment-timezone';
import type { JSX } from 'react';
import React from 'react';

import type { IStoredEmailTrigger } from '@feathr/blackbox';
import { FieldDataType, Flavors } from '@feathr/blackbox';
import type { IAttrOption } from '@feathr/extender/components/PredicateList/Predicates/utils';
import {
  getComparisonOptions,
  isExistenceComparison,
} from '@feathr/extender/components/PredicateList/Predicates/utils';
import type { RootStore } from '@feathr/extender/state';
import { TimeFormat } from '@feathr/hooks';

/** The store collections that id-valued trigger fields resolve their display names from. */
export type TTriggerIdCollections = Pick<
  RootStore,
  'Campaigns' | 'Events' | 'Forms' | 'Partners' | 'Segments' | 'Tags'
>;

const ID_FIELD_TO_COLLECTION: Record<string, keyof TTriggerIdCollections> = {
  cpn_id: 'Campaigns',
  e_id: 'Events',
  form_id: 'Forms',
  p_id: 'Partners',
  seg_id: 'Segments',
  tag_id: 'Tags',
  tag_ids: 'Tags',
};

/**
 * Human-readable label for a trigger's comparison, resolved from the same option set the editor
 * offers for the trigger's field. Undefined when the field or comparison is unknown.
 */
export function getTriggerComparisonLabel(
  trigger: IStoredEmailTrigger,
  field: IAttrOption | undefined,
  t: TFunction,
): string | undefined {
  const { comparison } = trigger;
  if (!field || !comparison) {
    return undefined;
  }
  // Stored date comparisons carry a `_date` / `_date_r` suffix; the option ids are the bare operator.
  const optionId = field.type === FieldDataType.date ? comparison.split('_')[0] : comparison;
  return getComparisonOptions(field, t).find(({ id }) => id === optionId)?.name;
}

/**
 * Human-readable rendering of a trigger's value: null for existence comparisons (no value to show),
 * an italic "empty" when unset, and otherwise a label appropriate to the field type — relative
 * dates, booleans, formatted dates, breadcrumb flavors, referenced model names, or the raw value.
 */
export function getTriggerValueLabel(
  trigger: IStoredEmailTrigger,
  t: TFunction,
  collections: TTriggerIdCollections,
): JSX.Element | string | number | null {
  const { comparison, value } = trigger;

  if (isExistenceComparison(comparison)) {
    return null;
  }

  if (value === undefined) {
    return <em>{t('empty')}</em>;
  }

  if (comparison?.includes('_r') && typeof value === 'number') {
    if (value === 0) {
      return t('today');
    }

    if (value >= 1) {
      return t('{{count}} day from now', { count: value });
    }

    return t('{{count}} day ago', { count: Math.abs(value) });
  }

  if (trigger.attr_type === 'boolean') {
    return value ? t('True') : t('False');
  }

  if (trigger.attr_type === 'date') {
    return moment(value as string | number).format(TimeFormat.shortWeekdayDateTime);
  }

  if (trigger.attr_against === 'flvr') {
    return Flavors[value as keyof typeof Flavors];
  }

  const collectionKey = trigger.attr_against
    ? ID_FIELD_TO_COLLECTION[trigger.attr_against]
    : undefined;
  if (collectionKey) {
    const collection = collections[collectionKey];
    const ids = value as string | string[];
    return Array.isArray(ids)
      ? ids.map((id) => collection.get(id).name).join(', ')
      : collection.get(ids).name;
  }

  if (trigger.attr_type === 'list' && Array.isArray(value)) {
    return value.join(', ');
  }

  return value as string | number;
}
